import { useEffect, useState, type ReactNode } from 'react'
import { isModuleEnabled, type Announcement, type CalendarEvent, type Campaign, type Project } from '@zetyar/domain'
import {
  ArrowRight,
  Briefcase,
  Calendar,
  ClipboardList,
  DollarSign,
  FileText,
  HeartHandshake,
  Handshake,
  MapPin,
  ShoppingCart,
  Megaphone,
  MessageSquare,
  Package,
  TrendingUp,
  Users,
  Video,
  CreditCard,
} from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Badge } from '@/presentation/components/ui/Badge'
import { Button } from '@/presentation/components/ui/Button'
import {
  createWebTenantContext,
  webAnnouncementRepository,
} from '@/infrastructure/repositories/announcements'
import { webMemberRepository } from '@/infrastructure/repositories/members'
import { webProjectRepository } from '@/infrastructure/repositories/projects'
import { webCampaignRepository } from '@/infrastructure/repositories/campaigns'
import { webSupplyRepository } from '@/infrastructure/repositories/supplies'
import { webChatRepository } from '@/infrastructure/repositories/chat'
import { webEventRepository } from '@/infrastructure/repositories/events'

export function DashboardPage() {
  const { profile, user, currentOrganization, currentMembership } = useApp()
  const [featuredAnnouncement, setFeaturedAnnouncement] = useState<Announcement | null>(null)
  const [memberCount, setMemberCount] = useState<number | null>(null)
  const [projectCount, setProjectCount] = useState<number | null>(null)
  const [campaignRevenue, setCampaignRevenue] = useState<number | null>(null)
  const [supplyCount, setSupplyCount] = useState<number | null>(null)
  const [projectsList, setProjectsList] = useState<Project[]>([])
  const [upcomingEvents, setUpcomingEvents] = useState<CalendarEvent[]>([])
  const [featuredCampaign, setFeaturedCampaign] = useState<Campaign | null>(null)
  const [hasUnreadChat, setHasUnreadChat] = useState(false)

  useEffect(() => {
    let isMounted = true

    const loadAnnouncement = async () => {
      if (!currentOrganization || !profile || !user) {
        setFeaturedAnnouncement(null)
        return
      }

      if (!isModuleEnabled(currentOrganization, 'announcements')) {
        setFeaturedAnnouncement(null)
        return
      }

      const context = createWebTenantContext({
        authUid: user.uid,
        personId: profile.id,
        organizationId: currentOrganization.id,
        membershipId: currentMembership?.id,
      })

      try {
        const announcements = await webAnnouncementRepository.listVisible(context, { limit: 1 })
        if (isMounted) setFeaturedAnnouncement(announcements.data[0] ?? null)
      } catch {
        if (isMounted) setFeaturedAnnouncement(null)
      }
    }

    void loadAnnouncement()

    return () => {
      isMounted = false
    }
  }, [currentOrganization, currentMembership, profile, user])

  useEffect(() => {
    let isMounted = true

    const loadStats = async () => {
      if (!currentOrganization || !profile || !user) return

      const context = createWebTenantContext({
        authUid: user.uid,
        personId: profile.id,
        organizationId: currentOrganization.id,
        membershipId: currentMembership?.id,
      })

      try {
        // Load active members count
        if (isModuleEnabled(currentOrganization, 'members')) {
          const members = await webMemberRepository.list(context, { limit: 100 })
          const activeMembers = members.filter((m) => m.status === 'active').length
          if (isMounted) setMemberCount(activeMembers)
        }

        // Load active projects count and list
        if (isModuleEnabled(currentOrganization, 'projects')) {
          const projects = await webProjectRepository.listVisible(context, { limit: 100 })
          const activeProjects = projects.filter((p) => p.status !== 'completed' && p.status !== 'cancelled')
          if (isMounted) {
            setProjectCount(activeProjects.length)
            setProjectsList(projects)
          }
        }

        // Load campaigns revenue
        if (isModuleEnabled(currentOrganization, 'campaigns')) {
          const campaigns = await webCampaignRepository.listVisible(context, { limit: 100 })
          const totalRaised = campaigns.reduce((acc, c) => acc + (c.raisedAmount || 0), 0)
          const activeCampaign = campaigns.find((c) => c.status === 'active')
          if (isMounted) {
            setCampaignRevenue(totalRaised)
            setFeaturedCampaign(activeCampaign ?? null)
          }
        }

        // Load agreements count
        if (isModuleEnabled(currentOrganization, 'agreements')) {
          const agreements = await webSupplyRepository.listAgreementInfos?.(context, 100) ?? []
          if (isMounted) setSupplyCount(agreements.length)
        }

        // Load upcoming events
        if (isModuleEnabled(currentOrganization, 'events')) {
          const events = await webEventRepository.listVisible(context, { limit: 20, fromDate: new Date().toISOString() })
          const activeEvents = events
            .filter((e) => e.status === 'published')
            .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
          if (isMounted) setUpcomingEvents(activeEvents)
        }

        // Check unread chat status
        if (isModuleEnabled(currentOrganization, 'chat') && webChatRepository.getUnreadChannelIds) {
          const unreadIds = await webChatRepository.getUnreadChannelIds(context)
          if (isMounted) setHasUnreadChat(unreadIds.length > 0)
        }
      } catch (error) {
        console.error('Error al cargar estadísticas del dashboard:', error)
      }
    }

    void loadStats()

    const handleChatRead = async () => {
      if (!currentOrganization || !profile || !user || !webChatRepository.getUnreadChannelIds) return
      if (!isModuleEnabled(currentOrganization, 'chat')) return
      const context = createWebTenantContext({
        authUid: user.uid,
        personId: profile.id,
        organizationId: currentOrganization.id,
        membershipId: currentMembership?.id,
      })
      try {
        const unreadIds = await webChatRepository.getUnreadChannelIds(context)
        if (isMounted) setHasUnreadChat(unreadIds.length > 0)
      } catch (error) {
        console.error('Error al actualizar chat no leído:', error)
      }
    }
    window.addEventListener('chat-read', handleChatRead)

    return () => {
      isMounted = false
      window.removeEventListener('chat-read', handleChatRead)
    }
  }, [currentMembership, currentOrganization, profile, user])

  const showMembersMetric = isModuleEnabled(currentOrganization, 'members')
  const showProjectsMetric = isModuleEnabled(currentOrganization, 'projects')
  const showCampaignsMetric = isModuleEnabled(currentOrganization, 'campaigns')
  const showAgreementsMetric = isModuleEnabled(currentOrganization, 'agreements')

  const hasAnyMetric = showMembersMetric || showProjectsMetric || showCampaignsMetric || showAgreementsMetric

  return (
    <div className="space-y-6">
      <section className="rounded-[24px] border border-neutral-200 bg-white p-6 shadow-card-elevated dark:border-neutral-800 dark:bg-neutral-900 md:p-7">
        <Badge variant="primary" className="mb-3">
          Espacio de comunidad privada
        </Badge>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 md:text-3xl">
          Hola, {profile?.displayName || 'Usuario'}
        </h1>
        <p className="mt-1 max-w-xl text-sm text-neutral-500 dark:text-neutral-400 md:text-base">
          Portal oficial de <strong>{currentOrganization?.name}</strong>. Revisa comunicados,
          actividades, proyectos activos, campañas y operaciones disponibles.
        </p>
      </section>

      {hasAnyMetric && (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {showMembersMetric && (
            <MetricCard
              icon={<Users className="w-6 h-6" />}
              label="Miembros activos"
              value={memberCount !== null ? memberCount.toString() : '...'}
              detail={memberCount !== null ? 'Registrados' : 'Cargando...'}
              detailIcon={<TrendingUp className="w-3 h-3" />}
              tone="primary"
            />
          )}
          {showProjectsMetric && (
            <MetricCard
              icon={<Briefcase className="w-6 h-6" />}
              label="Proyectos activos"
              value={projectCount !== null ? projectCount.toString() : '...'}
              detail={projectCount !== null ? 'En desarrollo' : 'Cargando...'}
              tone="neutral"
            />
          )}
          {showCampaignsMetric && (
            <MetricCard
              icon={<DollarSign className="w-6 h-6" />}
              label="Recaudacion campañas"
              value={campaignRevenue !== null ? `$${campaignRevenue.toLocaleString('es-CL')}` : '...'}
              detail={campaignRevenue !== null ? 'Total recaudado' : 'Cargando...'}
              tone="success"
            />
          )}
          {showAgreementsMetric && (
            <MetricCard
              icon={<Package className="w-6 h-6" />}
              label="Convenios"
              value={supplyCount !== null ? supplyCount.toString() : '...'}
              detail={supplyCount !== null ? `${supplyCount === 1 ? 'Convenio activo' : 'Convenios activos'}` : 'Cargando...'}
              tone="warning"
            />
          )}
        </section>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Actividades */}
          {isModuleEnabled(currentOrganization, 'events') && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary-600" />
                  <CardTitle>Próximas actividades</CardTitle>
                </div>
                <a href="#/events" className="text-xs font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1">
                  Ver todas <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {upcomingEvents.length > 0 ? (
                    <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                      {upcomingEvents.slice(0, 3).map((event) => {
                        const dateObj = new Date(event.startDate)
                        const day = dateObj.getDate()
                        const month = dateObj.toLocaleDateString('es-CL', { month: 'short', timeZone: 'America/Santiago' }).toUpperCase()
                        const time = dateObj.toLocaleTimeString('es-CL', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                          timeZone: 'America/Santiago',
                        })
                        return (
                          <div key={event.id} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-3">
                            <a href={`#/events?id=${event.id}`} className="flex items-center gap-3 min-w-0 flex-1 group">
                              <div className="flex flex-col items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-primary-50 dark:bg-primary-950/60 border border-primary-100 dark:border-primary-900/50 text-primary-700 dark:text-primary-300 group-hover:scale-105 transition-transform">
                                <span className="text-[10px] font-bold uppercase leading-none">{month}</span>
                                <span className="text-base font-extrabold leading-none mt-0.5">{day}</span>
                              </div>
                              <div className="min-w-0">
                                <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate">
                                  {event.title}
                                </h4>
                                <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                                  <span>{time} hrs</span>
                                  <span>•</span>
                                  {event.isVirtual ? (
                                    <span className="flex items-center gap-1 text-primary-600 dark:text-primary-400 font-medium">
                                      <Video className="w-3 h-3" /> Online
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 truncate">
                                      <MapPin className="w-3 h-3 shrink-0" /> {event.location || 'Presencial'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </a>
                            <a href={`#/events?id=${event.id}`}>
                              <Button size="sm" variant="outline">
                                Ver
                              </Button>
                            </a>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 italic py-4">
                      No hay actividades programadas en esta organización.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comunicado destacado */}
          {isModuleEnabled(currentOrganization, 'announcements') && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-primary-600" />
                  <CardTitle>Comunicado destacado</CardTitle>
                </div>
                <a href="#/announcements" className="text-xs font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1">
                  Ver todos <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </CardHeader>
              <CardContent>
                {featuredAnnouncement ? (
                  <div className="p-4 rounded-md bg-neutral-50 dark:bg-neutral-950 border border-neutral-200/80 dark:border-neutral-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="primary">Fijado</Badge>
                      <span className="text-xs text-neutral-400">
                        {new Date(featuredAnnouncement.publishedAt!).toLocaleDateString('es-CL')}
                      </span>
                    </div>
                    <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-base">
                      {featuredAnnouncement.title}
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2">
                      {featuredAnnouncement.content}
                    </p>
                    <div className="pt-2 flex items-center justify-between border-t border-neutral-200/60 dark:border-neutral-800">
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">
                        Publicado por {featuredAnnouncement.author?.displayName}
                      </span>
                      <a href="#/announcements">
                        <Button size="sm" variant="outline">
                          Leer comunicado
                        </Button>
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 italic py-4">
                    No hay comunicados destacados en esta organizacion.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Proyectos en desarrollo */}
          {isModuleEnabled(currentOrganization, 'projects') && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary-600" />
                  <CardTitle>Proyectos en desarrollo</CardTitle>
                </div>
                <a href="#/projects" className="text-xs font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1">
                  Ver proyectos <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {projectsList.length > 0 ? (
                    <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                      {projectsList.slice(0, 3).map((project) => (
                        <div key={project.id} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                              {project.title}
                            </h4>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-1">
                              {project.description || 'Sin descripcion'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 dark:bg-primary-950/40 dark:text-primary-300">
                              {project.progress}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 italic py-4">
                      No hay proyectos activos en esta organizacion.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {/* Campaña destacada */}
          {isModuleEnabled(currentOrganization, 'campaigns') && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <HeartHandshake className="w-5 h-5 text-emerald-600" />
                  <CardTitle>Campaña destacada</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {featuredCampaign ? (
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        {featuredCampaign.title}
                      </h4>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 line-clamp-2">
                        {featuredCampaign.description}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <div className="w-full bg-neutral-100 dark:bg-neutral-800 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${featuredCampaign.progressPercentage ?? 0}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[11px] text-neutral-500 dark:text-neutral-400 font-medium">
                        <span>
                          ${(featuredCampaign.raisedAmount || 0).toLocaleString('es-CL')} de ${(featuredCampaign.goalAmount || 0).toLocaleString('es-CL')}
                        </span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-bold">
                          {featuredCampaign.progressPercentage ?? 0}%
                        </span>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-neutral-100 dark:border-neutral-800 flex justify-end">
                      <a href="#/campaigns">
                        <Button size="sm" variant="primary">
                          Aportar
                        </Button>
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 italic py-2">
                    No hay campañas activas actualmente.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Accesos rápidos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Accesos rapidos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {isModuleEnabled(currentOrganization, 'events') && (
                  <QuickLink
                    href="#/events"
                    icon={<Calendar className="w-5 h-5 text-primary-600" />}
                    label="Actividades"
                  />
                )}
                {isModuleEnabled(currentOrganization, 'chat') && (
                  <QuickLink
                    href="#/chat"
                    icon={<MessageSquare className="w-5 h-5 text-primary-600" />}
                    label="Ir al chat"
                    hasBadge={hasUnreadChat}
                  />
                )}
                {isModuleEnabled(currentOrganization, 'documents') && (
                  <QuickLink href="#/documents" icon={<FileText className="w-5 h-5 text-primary-600" />} label="Documentos" />
                )}
                {isModuleEnabled(currentOrganization, 'members') && (
                  <QuickLink href="#/members" icon={<Users className="w-5 h-5 text-primary-600" />} label="Miembros" />
                )}
                {isModuleEnabled(currentOrganization, 'campaigns') && (
                  <QuickLink href="#/campaigns" icon={<HeartHandshake className="w-5 h-5 text-primary-600" />} label="Campañas" />
                )}
                {isModuleEnabled(currentOrganization, 'dues') && (
                  <QuickLink href="#/dues" icon={<CreditCard className="w-5 h-5 text-primary-600" />} label="Cuotas" />
                )}
                {isModuleEnabled(currentOrganization, 'agreements') && (
                  <QuickLink href="#/agreements" icon={<Handshake className="w-5 h-5 text-primary-600" />} label="Convenios" />
                )}
                {isModuleEnabled(currentOrganization, 'sales') && (
                  <QuickLink href="#/sales" icon={<ShoppingCart className="w-5 h-5 text-primary-600" />} label="Ventas" />
                )}
                {isModuleEnabled(currentOrganization, 'requests') && (
                  <QuickLink href="#/requests" icon={<ClipboardList className="w-5 h-5 text-primary-600" />} label="Solicitudes" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  )
}

function MetricCard({
  icon,
  label,
  value,
  detail,
  detailIcon,
  tone,
}: {
  readonly icon: ReactNode
  readonly label: string
  readonly value: string
  readonly detail: string
  readonly detailIcon?: ReactNode
  readonly tone: 'primary' | 'neutral' | 'success' | 'warning'
}) {
  const toneClasses = {
    primary: 'bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-200',
    neutral: 'bg-neutral-100 dark:bg-neutral-800 text-primary-700 dark:text-primary-200',
    success: 'bg-success-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300',
    warning: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-300',
  }

  return (
    <Card className="p-4 flex items-center gap-3 min-w-0">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-[13px] ${toneClasses[tone]}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium truncate" title={label}>{label}</p>
        <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100 truncate" title={value}>{value}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium flex items-center gap-1 mt-0.5 truncate" title={detail}>
          {detailIcon}
          {detail}
        </p>
      </div>
    </Card>
  )
}

function QuickLink({
  href,
  icon,
  label,
  hasBadge = false,
}: {
  readonly href: string
  readonly icon: ReactNode
  readonly label: string
  readonly hasBadge?: boolean
}) {
  return (
    <a
      href={href}
      className="relative p-3 rounded-md border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800 flex flex-col items-center gap-1.5 text-center transition-colors"
    >
      {icon}
      <span className="text-xs font-medium text-neutral-700 dark:text-neutral-200">{label}</span>
      {hasBadge && (
        <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-rose-500 animate-pulse shadow-glow" aria-label="Alerta" />
      )}
    </a>
  )
}
